var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { validateCredentials, connect, updateOffices, triggerSync, updateSettings, disconnect, getWebhookStatus, setupWebhook, removeWebhook } from '../services/ServiceTradeService';
// Hook to validate ServiceTrade credentials
export function useValidateCredentialsMutation() {
    return useMutation({
        mutationFn: (_a) => __awaiter(this, [_a], void 0, function* ({ username, password }) {
            const response = yield validateCredentials(username, password);
            if (response.error) {
                throw new Error(response.error.message);
            }
            if (!response.data) {
                throw new Error('No data returned from validation');
            }
            return response.data;
        }),
        onError: (error) => {
            toast.error(error.message || 'Failed to validate credentials. Please try again.');
        }
    });
}
// Hook to connect ServiceTrade account
export function useConnectMutation() {
    return useMutation({
        mutationFn: (_a) => __awaiter(this, [_a], void 0, function* ({ username, password, vendorId, officeIds, authToken }) {
            const response = yield connect(username, password, vendorId, officeIds, authToken);
            if (response.error) {
                throw new Error(response.error.message);
            }
            if (!response.data) {
                throw new Error('No data returned from connection');
            }
            return response.data;
        }),
        onSuccess: () => {
            toast.success('Successfully connected to ServiceTrade!');
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to connect to ServiceTrade. Please try again.');
        }
    });
}
// Hook to update selected offices
export function useUpdateOfficesMutation() {
    return useMutation({
        mutationFn: (officeIds) => __awaiter(this, void 0, void 0, function* () {
            const response = yield updateOffices(officeIds);
            if (response.error) {
                throw new Error(response.error.message);
            }
            if (!response.data) {
                throw new Error('No data returned from office update');
            }
            return response.data;
        }),
        onSuccess: (data) => {
            toast.success(data.message || 'Offices updated successfully!');
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to update offices. Please try again.');
        }
    });
}
// Hook to trigger manual sync
export function useTriggerSyncMutation() {
    return useMutation({
        mutationFn: (officeIds) => __awaiter(this, void 0, void 0, function* () {
            const response = yield triggerSync(officeIds);
            if (response.error) {
                throw new Error(response.error.message);
            }
            if (!response.data) {
                throw new Error('No data returned from sync trigger');
            }
            return response.data;
        }),
        onSuccess: () => {
            toast.success('Sync started successfully! Import may take several minutes.');
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to trigger sync. Please try again.');
        }
    });
}
// Hook to update ServiceTrade settings
// Note: Settings use local component state, not React Query cache
// Optimistic behavior is achieved via immediate local state updates in the component
// This mutation confirms the save with the server and shows toast feedback
export function useUpdateSettingsMutation() {
    return useMutation({
        mutationFn: (settings) => __awaiter(this, void 0, void 0, function* () {
            const response = yield updateSettings(settings);
            if (response.error) {
                throw new Error(response.error.message);
            }
            return response.data;
        }),
        onSuccess: () => {
            toast.success('Settings saved successfully!');
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to save settings. Please try again.');
            // Note: Component should handle rolling back local state on error
        }
    });
}
// Hook to disconnect from ServiceTrade
export function useDisconnectMutation() {
    return useMutation({
        mutationFn: () => __awaiter(this, void 0, void 0, function* () {
            const response = yield disconnect();
            if (response.error) {
                throw new Error(response.error.message);
            }
            if (!response.data) {
                throw new Error('No data returned from disconnect');
            }
            return response.data;
        }),
        onSuccess: () => {
            toast.success('Disconnected from ServiceTrade');
            // Reload page to show empty state
            window.location.reload();
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to disconnect. Please try again.');
        }
    });
}
// Hook to get webhook status
export function useWebhookStatus(enabled = false) {
    return useQuery({
        queryKey: ['serviceTrade', 'webhook', 'status'],
        queryFn: () => __awaiter(this, void 0, void 0, function* () {
            const response = yield getWebhookStatus();
            if (response.error) {
                throw new Error(response.error.message);
            }
            if (!response.data) {
                throw new Error('No data returned from webhook status');
            }
            return response.data;
        }),
        enabled,
        refetchOnWindowFocus: false,
        retry: 1
    });
}
// Hook to setup webhook
export function useSetupWebhookMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => __awaiter(this, void 0, void 0, function* () {
            const response = yield setupWebhook();
            if (response.error) {
                throw new Error(response.error.message);
            }
            if (!response.data) {
                throw new Error('No data returned from webhook setup');
            }
            return response.data;
        }),
        // Optimistic update: Show webhook as configured immediately
        onMutate: () => __awaiter(this, void 0, void 0, function* () {
            yield queryClient.cancelQueries({ queryKey: ['serviceTrade', 'webhook', 'status'] });
            const previousWebhookStatus = queryClient.getQueryData(['serviceTrade', 'webhook', 'status']);
            // Optimistically update webhook status
            queryClient.setQueryData(['serviceTrade', 'webhook', 'status'], {
                success: true,
                webhook: {
                    id: 0, // Temporary ID
                    url: 'Configuring...',
                    enabled: true,
                    created_at: new Date().toISOString()
                }
            });
            return { previousWebhookStatus };
        }),
        onError: (error, variables, context) => {
            // Roll back to previous state on error
            if (context === null || context === void 0 ? void 0 : context.previousWebhookStatus) {
                queryClient.setQueryData(['serviceTrade', 'webhook', 'status'], context.previousWebhookStatus);
            }
            toast.error(error.message || 'Failed to setup webhook. Please try again.');
        },
        onSuccess: () => {
            toast.success('Webhook configured successfully!');
        },
        onSettled: () => {
            // Refetch to ensure consistency with server
            queryClient.invalidateQueries({ queryKey: ['serviceTrade', 'webhook', 'status'] });
        }
    });
}
// Hook to remove webhook
export function useRemoveWebhookMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => __awaiter(this, void 0, void 0, function* () {
            const response = yield removeWebhook();
            if (response.error) {
                throw new Error(response.error.message);
            }
            if (!response.data) {
                throw new Error('No data returned from webhook removal');
            }
            return response.data;
        }),
        // Optimistic update: Show webhook as removed immediately
        onMutate: () => __awaiter(this, void 0, void 0, function* () {
            yield queryClient.cancelQueries({ queryKey: ['serviceTrade', 'webhook', 'status'] });
            const previousWebhookStatus = queryClient.getQueryData(['serviceTrade', 'webhook', 'status']);
            // Optimistically update webhook status to null (removed)
            queryClient.setQueryData(['serviceTrade', 'webhook', 'status'], {
                success: true,
                webhook: null
            });
            return { previousWebhookStatus };
        }),
        onError: (error, variables, context) => {
            // Roll back to previous state on error
            if (context === null || context === void 0 ? void 0 : context.previousWebhookStatus) {
                queryClient.setQueryData(['serviceTrade', 'webhook', 'status'], context.previousWebhookStatus);
            }
            toast.error(error.message || 'Failed to remove webhook. Please try again.');
        },
        onSuccess: () => {
            toast.success('Webhook removed successfully');
        },
        onSettled: () => {
            // Refetch to ensure consistency with server
            queryClient.invalidateQueries({ queryKey: ['serviceTrade', 'webhook', 'status'] });
        }
    });
}
